import React, { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { cn } from '@/lib/utils';
import { IsolatedHtmlContent } from './IsolatedHtmlContent';
import 'katex/dist/katex.min.css';

interface BotResponseProps {
  content: string;
  className?: string;
}

const BotResponse: React.FC<BotResponseProps> = memo(({ content, className }) => {
  // Enhanced HTML detection for agent responses
  const isHtmlContent = content.includes('<div style=') || 
                       content.includes('background: linear-gradient') ||
                       content.includes('<h2 style=') ||
                       content.includes('<img src=') ||
                       content.includes('rgba(') ||
                       content.includes('box-shadow:') ||
                       content.includes('border-radius:');
  
  if (isHtmlContent) {
    return (
      <div className={cn('w-full my-4', className)}>
        <IsolatedHtmlContent 
          html={content}
          style={{ minHeight: '150px', height: 'auto' }}
        />
      </div>
    );
  }

  return (
    <div className={cn('prose prose-gray max-w-none dark:prose-invert prose-pre:bg-muted prose-pre:border prose-pre:border-border', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{
          table: ({ node, ...props }) => (
            <div className="my-6 overflow-x-auto rounded-lg border border-border shadow-sm custom-table-scrollbar">
              <style>{`
                .custom-table-scrollbar::-webkit-scrollbar {
                  height: 4px;
                }
                .custom-table-scrollbar::-webkit-scrollbar-track {
                  background: transparent;
                }
                .custom-table-scrollbar::-webkit-scrollbar-thumb {
                  background: rgba(156, 163, 175, 0.3);
                  border-radius: 4px;
                }
                .custom-table-scrollbar {
                  scrollbar-width: thin;
                  scrollbar-color: rgba(156, 163, 175, 0.3) transparent;
                }
              `}</style>
              <table
                className="min-w-max border-collapse bg-card"
                {...props}
              />
            </div>
          ),
          thead: ({ node, ...props }) => (
            <thead className="bg-muted/50" {...props} />
          ),
          tbody: ({ node, ...props }) => (
            <tbody className="divide-y divide-border" {...props} />
          ),
          tr: ({ node, ...props }) => (
            <tr className="transition-colors hover:bg-muted/30" {...props} />
          ),
          th: ({ node, ...props }) => (
            <th
              className="px-6 py-3 text-left text-sm font-semibold text-foreground border-b border-border"
              {...props}
            />
          ),
          td: ({ node, ...props }) => (
            <td
              className="px-6 py-4 text-left text-sm text-muted-foreground border-b border-border last:border-b-0"
              {...props}
            />
          ),
          code({ node, className, children, ...props }: { node?: any; className?: string; children?: React.ReactNode; [key: string]: any }) {
            const match = /language-(\w+)/.exec(className || '');
            const isCodeBlock = match !== null && !('inline' in props && props.inline);
            return isCodeBlock ? (
              <pre className="rounded-lg bg-muted p-4 border border-border my-4 overflow-x-auto">
                <code className={className} {...props}>
                  {children}
                </code>
              </pre>
            ) : (
              <code
                className="rounded bg-muted px-2 py-1 text-sm font-mono border border-border"
                {...props}
              >
                {children}
              </code>
            );
          },
          a: ({ node, ...props }) => (
            <a
              className="text-primary hover:underline underline-offset-4 transition-colors"
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            />
          ),
          p: ({ node, ...props }) => (
            <p className="my-2 text-foreground leading-7" {...props} />
          ),
          h1: ({ node, ...props }) => (
            <h1 className="text-3xl font-bold mt-8 mb-4 text-foreground" {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h2 className="text-2xl font-semibold mt-6 mb-3 text-foreground" {...props} />
          ),
          h3: ({ node, ...props }) => (
            <h3 className="text-xl font-medium mt-5 mb-2 text-foreground" {...props} />
          ),
          ul: ({ node, ...props }) => (
            <ul className="my-4 ml-6 list-disc space-y-2 text-foreground" {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol className="my-4 ml-6 list-decimal space-y-2 text-foreground" {...props} />
          ),
          li: ({ node, ...props }) => (
            <li className="text-foreground leading-7" {...props} />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote className="my-4 border-l-4 border-primary pl-4 text-muted-foreground italic bg-muted/30 py-2 rounded-r" {...props} />
          ),
          // Math rendering components
          span: ({ node, className, children, ...props }: { node?: any; className?: string; children?: React.ReactNode; [key: string]: any }) => {
            // Handle KaTeX math spans
            if (className?.includes('katex')) {
              return <span className={className} {...props}>{children}</span>;
            }
            return <span {...props}>{children}</span>;
          },
          div: ({ node, className, children, ...props }: { node?: any; className?: string; children?: React.ReactNode; [key: string]: any }) => {
            // Handle KaTeX math displays
            if (className?.includes('katex-display')) {
              return <div className={`${className} my-4`} {...props}>{children}</div>;
            }
            return <div {...props}>{children}</div>;
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
});

BotResponse.displayName = 'BotResponse';

export default BotResponse;